import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import SEO from "../components/SEO";
import { ArrowRight, Loader2, LayoutGrid, User as UserIcon, X, Search } from "lucide-react";

/**
 * V2.0.88 — Public /gallery — infinite-scroll grid of every shared
 * design where the owner started from an uploaded sketch. Each card
 * shows the composed before/after PNG (via /api/share/{slug}/og-image.png).
 *
 * Route: `/gallery`. No auth.
 */
export default function GalleryPage() {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const sentinelRef = useRef(null);
  const [featured, setFeatured] = useState(null); // top designer to feature
  const [searchParams, setSearchParams] = useSearchParams();
  const filterUserName = searchParams.get("designer") || null;
  const [searchInput, setSearchInput] = useState(searchParams.get("q") || "");
  const qFilter = searchParams.get("q") || null;
  const styleFilter = searchParams.get("style") || null;

  const fetchPage = async (nextCursor) => {
    const r = await axios.get(`${API}/discover/gallery`, {
      params: {
        limit: 12,
        ...(nextCursor ? { cursor: nextCursor } : {}),
        ...(filterUserName ? { designer_name: filterUserName } : {}),
        ...(qFilter ? { q: qFilter } : {}),
        ...(styleFilter ? { style: styleFilter } : {}),
      },
    });
    return r.data;
  };

  // V2.0.89 — Pull the top-1 transformation to feature the designer.
  // Silent-fail: no top designer means no callout.
  useEffect(() => {
    if (filterUserName) { setFeatured(null); return; }
    let cancel = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/discover/best-transformations`, { params: { limit: 1 } });
        if (!cancel && (r.data?.items || []).length > 0) {
          setFeatured(r.data.items[0]);
        }
      } catch { /* silent */ }
    })();
    return () => { cancel = true; };
  }, [filterUserName]);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true); setError(""); setItems([]); setCursor(null); setDone(false);
      try {
        const d = await fetchPage(null);
        if (cancel) return;
        setItems(d.items || []);
        setCursor(d.next_cursor);
        setDone(!d.next_cursor);
      } catch (err) {
        if (!cancel) setError(err?.response?.data?.detail || err?.message || "Could not load gallery");
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterUserName, qFilter, styleFilter]);

  useEffect(() => {
    if (!sentinelRef.current || done || loading) return;
    const io = new IntersectionObserver(async ([entry]) => {
      if (!entry.isIntersecting || loadingMore || done) return;
      setLoadingMore(true);
      try {
        const d = await fetchPage(cursor);
        setItems((prev) => [...prev, ...(d.items || [])]);
        setCursor(d.next_cursor);
        setDone(!d.next_cursor);
      } catch (err) {
        // silent — user can scroll to retry
      } finally {
        setLoadingMore(false);
      }
    }, { rootMargin: "300px" });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [cursor, done, loading, loadingMore]);

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="gallery-page">
      <SEO
        title="Gallery — sketch to 3D transformations"
        description="Browse every hand-drawn floor plan real people turned into a full 3D home with Blueprint AI."
        path="/gallery"
      />
      <header className="border-b border-zinc-800 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="font-mono text-sm text-brass-400 hover:text-brass-300">
          // BLUEPRINT_AI
        </Link>
        <div className="flex items-center gap-4">
          <Link
            to="/best-transformations"
            className="hidden md:inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-zinc-400 hover:text-brass-300"
            data-testid="gallery-showcase-link"
          >
            Weekly top 5
          </Link>
          <Link
            to="/"
            className="font-mono text-[10px] uppercase tracking-widest text-brass-300 hover:text-brass-200 border border-brass-700/60 px-3 py-1.5"
            data-testid="gallery-cta"
          >
            Design your own →
          </Link>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <section className="max-w-3xl">
          <div className="font-mono text-[10px] uppercase tracking-widest text-brass-400 mb-3">
            // Gallery · sketch to 3D
          </div>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight mb-3">
            {filterUserName ? (
              <>Designs by <span className="text-brass-400">{filterUserName}</span></>
            ) : (
              <>Every home that started as a sketch.</>
            )}
          </h1>
          <p className="text-zinc-400 text-base leading-relaxed">
            Real designs from real users. Each one started as a hand-drawn floor
            plan, a phone photo of a sketch, or a scanned PDF — then Blueprint AI
            turned it into an editable 3D BIM home.
          </p>
          {filterUserName && (
            <button
              type="button"
              onClick={() => setSearchParams({})}
              data-testid="gallery-clear-filter"
              className="mt-3 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest text-brass-300 hover:text-brass-200 border-b border-brass-700/60 hover:border-brass-500 pb-0.5"
            >
              <X className="w-3 h-3" /> Clear designer filter
            </button>
          )}
          {/* V2.0.90 — Search bar (name contains) + style filter. */}
          <form
            className="mt-6 flex flex-wrap gap-2 items-center"
            onSubmit={(e) => {
              e.preventDefault();
              const next = new URLSearchParams(searchParams);
              if (searchInput.trim()) next.set("q", searchInput.trim());
              else next.delete("q");
              setSearchParams(next);
            }}
          >
            <div className="flex items-center flex-1 min-w-[200px] max-w-md border border-zinc-800 focus-within:border-brass-500 bg-zinc-900/40 px-3">
              <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search designs by name..."
                data-testid="gallery-search-input"
                className="bg-transparent px-2 py-2 outline-none text-sm text-zinc-100 flex-1 placeholder:text-zinc-600"
              />
            </div>
            <select
              value={styleFilter || ""}
              onChange={(e) => {
                const next = new URLSearchParams(searchParams);
                if (e.target.value) next.set("style", e.target.value);
                else next.delete("style");
                setSearchParams(next);
              }}
              data-testid="gallery-style-filter"
              className="bg-zinc-900 border border-zinc-800 focus:border-brass-500 px-3 py-2 text-sm text-zinc-100 outline-none font-mono text-[11px]"
            >
              <option value="">All styles</option>
              <option value="Modern">Modern</option>
              <option value="Farmhouse">Farmhouse</option>
              <option value="Colonial">Colonial</option>
              <option value="Craftsman">Craftsman</option>
              <option value="Mountain Cabin">Mountain Cabin</option>
              <option value="Contemporary">Contemporary</option>
              <option value="Cape Cod">Cape Cod</option>
            </select>
            <button
              type="submit"
              data-testid="gallery-search-submit"
              className="border border-brass-700/60 hover:border-brass-500 text-brass-300 font-mono text-[10px] uppercase tracking-widest px-4 py-2"
            >
              Search
            </button>
            {(qFilter || styleFilter) && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); setSearchParams({}); }}
                data-testid="gallery-clear-search"
                className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-zinc-300"
              >
                Clear
              </button>
            )}
          </form>
        </section>

        {/* V2.0.89 — Featured Designer callout. Only shown on the
            unfiltered view and when we have a top item. */}
        {featured && !filterUserName && (
          <section
            data-testid="gallery-featured-designer"
            className="border border-brass-500/40 bg-gradient-to-br from-brass-950/30 to-transparent px-6 py-5 flex flex-col md:flex-row items-start md:items-center gap-4"
          >
            <div className="w-12 h-12 rounded-full bg-brass-600 text-black grid place-items-center font-display text-xl shrink-0">
              {(featured.owner_display_name || "A").charAt(0).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 mb-1 flex items-center gap-1.5">
                <UserIcon className="w-3 h-3" /> // Featured designer
              </div>
              <div className="font-display text-xl text-zinc-100">
                {featured.owner_display_name || "Anonymous"}
              </div>
              <div className="font-body text-xs text-zinc-500 mt-0.5">
                Top transformation this week — {featured.impressions?.toLocaleString() || 0} views
              </div>
            </div>
            <Link
              to={`/gallery?designer=${encodeURIComponent(featured.owner_display_name || "")}`}
              data-testid="gallery-featured-designer-link"
              className="inline-flex items-center gap-1.5 border border-brass-700/60 hover:border-brass-500 text-brass-300 hover:text-brass-200 font-mono text-[10px] uppercase tracking-widest px-4 py-2 transition-colors"
            >
              See more from {featured.owner_display_name?.split(" ")[0] || "them"} <ArrowRight className="w-3 h-3" />
            </Link>
          </section>
        )}

        {loading && (
          <div className="flex items-center gap-2 text-zinc-500 font-mono text-xs" data-testid="gallery-loading">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading gallery…
          </div>
        )}
        {error && (
          <div className="font-mono text-xs text-rose-400 border border-rose-800/60 bg-rose-950/40 px-4 py-3">
            {error}
          </div>
        )}

        {!loading && !error && items.length === 0 && (
          <div className="border border-zinc-800 px-6 py-16 text-center" data-testid="gallery-empty">
            <LayoutGrid className="w-10 h-10 text-brass-400 mx-auto mb-3" />
            <div className="font-display text-xl text-zinc-100 mb-1">Empty for now</div>
            <div className="text-zinc-500 text-sm max-w-md mx-auto">
              Upload a floor plan sketch and share your design to be the first one here.
            </div>
          </div>
        )}

        {items.length > 0 && (
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="gallery-grid">
            {items.map((item) => (
              <GalleryCard key={item.share_slug} item={item} />
            ))}
          </ul>
        )}

        <div ref={sentinelRef} className="h-4" data-testid="gallery-sentinel" />
        {loadingMore && (
          <div className="flex items-center justify-center gap-2 text-zinc-500 font-mono text-xs py-4" data-testid="gallery-loading-more">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading more…
          </div>
        )}
        {done && items.length > 0 && (
          <div className="text-center text-zinc-500 font-mono text-[10px] uppercase tracking-widest py-6">
            // End of gallery — {items.length} shown
          </div>
        )}
      </main>
    </div>
  );
}

function GalleryCard({ item }) {
  return (
    <li className="border border-zinc-800 hover:border-brass-500/40 group transition-colors" data-testid={`gallery-card-${item.share_slug}`}>
      <Link to={item.share_url} className="block">
        <div className="aspect-[16/9] bg-zinc-900 overflow-hidden">
          <img
            src={`${API}/share/${item.share_slug}/og-image.png`}
            alt={`Before/after transformation of ${item.name || "a home design"}`}
            className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform"
            loading="lazy"
          />
        </div>
        <div className="p-4 space-y-2">
          <div className="flex items-baseline justify-between gap-2">
            <div className="font-display text-lg text-zinc-100 truncate">{item.name || "Untitled"}</div>
            {item.style && (
              <div className="font-mono text-[9px] uppercase tracking-widest text-brass-400 shrink-0">
                {item.style}
              </div>
            )}
          </div>
          <div className="flex gap-2 text-[10px] font-mono text-zinc-500">
            <span>{(item.square_footage || 0).toLocaleString()} sqft</span>
            <span>•</span>
            <span>{item.bedrooms ?? "?"} BR</span>
            <span>•</span>
            <span>{item.bathrooms ?? "?"} BA</span>
          </div>
          <div className="inline-flex items-center gap-1 font-mono text-[10px] uppercase tracking-widest text-brass-300 group-hover:text-brass-200 pt-1">
            See design <ArrowRight className="w-3 h-3" />
          </div>
        </div>
      </Link>
    </li>
  );
}
